import React, { useState, useEffect, useRef } from "react";
import { features } from "../../../config/featureConfig";
import { PiPhoneOutgoingFill, PiPhoneIncomingFill } from "react-icons/pi";
import { MdPhoneMissed } from "react-icons/md";
import { HiPhoneMissedCall } from "react-icons/hi";
import CallLogService from "../../Services/CallLogService";

const LeadCallHistory = ({ docId }) => {
  const [callStatus, setCallStatus] = useState(true); // true for SIM, false for IVR
  const [data, setData] = useState([]);
  const [nextId, setNextId] = useState(null);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef(null);

  const callTypeIcons = {
    INCOMING: <PiPhoneIncomingFill className="text-green-500 text-lg" />,
    OUTGOING: <PiPhoneOutgoingFill className="text-blue-500 text-lg" />,
    MISSED: <MdPhoneMissed className="text-red-500 text-lg" />,
    RNR: <HiPhoneMissedCall className="text-yellow-500 text-lg" />,
  };

  const fetchCallHistory = async (next = null, append = false) => {
    if (loading) return;
    setLoading(true);

    const body = {
        leadDocId: docId,
      isIvr: callStatus ? "false" : "true",
      limit: 10,
      next,
      direction: "next",
    };

    try {
      const response = await CallLogService.getLeadCallLogsHistory(body);
      const newData = response?.data?.data || [];
      const pagination = response?.data?.pagination || {};

      setData((prev) => (append ? [...prev, ...newData] : newData));
      setNextId(pagination?.nextId || null);
    } catch (error) {
      console.log("Error fetching call logs", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load + re-fetch when SIM/IVR toggle changes
  useEffect(() => {
    fetchCallHistory();
  }, [callStatus]);

  // Infinite scroll handler
  const handleScroll = () => {
    if (!containerRef.current || loading || !nextId) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      fetchCallHistory(nextId, true);
    }
  };

  useEffect(() => {
    const el = containerRef.current;
    if (el) el.addEventListener("scroll", handleScroll);
    return () => {
      if (el) el.removeEventListener("scroll", handleScroll);
    };
  }, [nextId, loading]);

  return (
    <div className="bg-white shadow-md rounded-lg w-full p-4 h-96 flex flex-col">
      {/* Header */}
      <div className="bg-slate-100 p-2 mb-3 rounded-md">
        <div className="text-customGreen text-lg font-semibold">
          Call Log History
        </div>
      </div>

      {/* Toggle buttons */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className={`px-3 py-1 rounded-full cursor-pointer text-sm font-medium transition ${
            callStatus
              ? "bg-customGreen text-white"
              : "bg-slate-200 text-gray-700"
          }`}
          onClick={() => setCallStatus(true)}
        >
          SIM Calls
        </div>
        {features?.ivrCalls && (
          <div
            className={`px-3 py-1 rounded-full cursor-pointer text-sm font-medium transition ${
              !callStatus
                ? "bg-customGreen text-white"
                : "bg-slate-200 text-gray-700"
            }`}
            onClick={() => setCallStatus(false)}
          >
            IVR Calls
          </div>
        )}
      </div>

      {/* Call logs */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto custom-scroll pr-5"
      >
        {data.length === 0 && !loading && (
          <div className="text-center text-gray-500 text-sm py-4">
            No call logs found
          </div>
        )}

        {data.map((item, idx) => (
          <div
            key={item.id || idx}
            className="flex justify-between items-center border-b py-2"
          >
            <div className="flex items-center gap-2">
              {callTypeIcons[item.callType] || (
                <MdPhoneMissed className="text-gray-400 text-lg" />
              )}
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">{item.time}</span>
                <span className="text-xs text-gray-500">{item.duration}</span>
                <span className="text-sm text-gray-700 font-medium">
                  {item.user?.replace("user: ", "")}
                </span>
              </div>
            </div>
            <div className="text-xs text-gray-500">{item.date}</div>
          </div>
        ))}

        {loading && (
          <div className="text-center text-gray-500 text-sm py-2">
            Loading...
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadCallHistory;
